import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useStatsTimeSeries } from "../hooks/useStatsTimeSeries";
import { DIFFICULTY_LABELS, GENRE_LABELS, Genre, TopicStats } from "../types";

// 日付範囲を計算
const getDateRange = (days: number): { from: string; to: string } => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - days + 1);

  return {
    from: from.toISOString().split("T")[0],
    to: today.toISOString().split("T")[0],
  };
};

// カラーパレット
const COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7c7c",
  "#a4de6c",
  "#d0ed57",
  "#83a6ed",
  "#8dd1e1",
];

const ResponseStatsChart: React.FC = () => {
  const [period, setPeriod] = useState<number>(30);
  const [viewType, setViewType] = useState<"total" | "genre" | "difficulty" | "topic">("total");

  const { from, to } = getDateRange(period);
  const { data, isLoading, error } = useStatsTimeSeries(from, to);

  const handlePeriodChange = (event: React.MouseEvent<HTMLElement>, newPeriod: number | null) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  const handleViewTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newViewType: "total" | "genre" | "difficulty" | "topic" | null
  ) => {
    if (newViewType !== null) {
      setViewType(newViewType);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">グラフデータの取得に失敗しました</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.dailyData || data.dailyData.length === 0) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">表示するデータがありません</Alert>
        </CardContent>
      </Card>
    );
  }

  // 日付範囲を全て埋める（データがない日も0として表示）
  const fillDateRange = (startDate: string, endDate: string) => {
    const dates: string[] = [];
    const current = new Date(startDate);
    const end = new Date(endDate);

    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const allDates = fillDateRange(from, to);
  const dataByDate = new Map(data.dailyData.map((day) => [day.date, day]));

  // 表示するジャンル・難易度のリストを先に取得
  const genres = Array.from(
    new Set(data.dailyData.flatMap((day) => Object.keys(day.byGenre || {})))
  ).sort();

  const difficulties = [1, 2, 3, 4, 5].map(
    (d) => DIFFICULTY_LABELS[d as keyof typeof DIFFICULTY_LABELS]
  );

  // 問題別：期間内の累計回答数を計算してトップ5を取得
  const topicTotals: { [id: string]: { total: number; stats: TopicStats } } = {};
  data.dailyData.forEach((day) => {
    Object.entries(day.byTopic || {}).forEach(([id, stats]) => {
      if (!topicTotals[id]) {
        topicTotals[id] = { total: 0, stats };
      }
      topicTotals[id].total += stats.count;
    });
  });

  const topTopics = Object.entries(topicTotals)
    .sort(([, a], [, b]) => b.total - a.total)
    .slice(0, 5)
    .map(([id, { stats }]) => ({
      id,
      label:
        stats.contentPreview.substring(0, 20) + (stats.contentPreview.length > 20 ? "..." : ""),
    }));

  // 合計回答数データの整形
  const totalChartData = allDates.map((date) => {
    const dayData = dataByDate.get(date);
    return {
      date,
      回答数: dayData?.totalResponses || 0,
    };
  });

  const genreLabels = genres.map((genre) => GENRE_LABELS[genre as Genre] || genre);

  // ジャンル別データの整形
  const genreChartData = allDates.map((date) => {
    const dayData = dataByDate.get(date);
    const point: Record<string, string | number> = { date };
    genres.forEach((genre) => {
      const label = GENRE_LABELS[genre as Genre] || genre;
      point[label] = dayData?.byGenre?.[genre] || 0;
    });
    return point;
  });

  // 難易度別データの整形（難易度1〜5の順序を保証）
  const difficultyChartData = allDates.map((date) => {
    const dayData = dataByDate.get(date);
    // 難易度1〜5の順序でオブジェクトを構築
    const point: Record<string, string | number> = {
      date,
      [DIFFICULTY_LABELS[1]]: dayData?.byDifficulty?.[1] || 0,
      [DIFFICULTY_LABELS[2]]: dayData?.byDifficulty?.[2] || 0,
      [DIFFICULTY_LABELS[3]]: dayData?.byDifficulty?.[3] || 0,
      [DIFFICULTY_LABELS[4]]: dayData?.byDifficulty?.[4] || 0,
      [DIFFICULTY_LABELS[5]]: dayData?.byDifficulty?.[5] || 0,
    };
    return point;
  });

  // 問題別データの整形（トップ5問題の推移）
  const topicChartData = allDates.map((date) => {
    const dayData = dataByDate.get(date);
    const point: Record<string, string | number> = { date };
    topTopics.forEach(({ id, label }) => {
      point[label] = dayData?.byTopic?.[id]?.count || 0;
    });
    return point;
  });

  const topicLabels = topTopics.map((so) => so.label);

  const chartData =
    viewType === "total"
      ? totalChartData
      : viewType === "genre"
        ? genreChartData
        : viewType === "difficulty"
          ? difficultyChartData
          : topicChartData;
  const dataKeys =
    viewType === "total"
      ? ["回答数"]
      : viewType === "genre"
        ? genreLabels
        : viewType === "difficulty"
          ? difficulties
          : topicLabels;

  // X軸の日付表示間隔を期間に応じて調整
  const getXAxisInterval = () => {
    if (period === 7) return 0; // 7日間: 全日表示
    if (period === 30) return Math.floor(30 / 6); // 30日間: 約6個表示
    return Math.floor(90 / 6); // 90日間: 約6個表示
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">回答数推移</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={handleViewTypeChange}
              size="small"
            >
              <ToggleButton value="total">合計</ToggleButton>
              <ToggleButton value="genre">ジャンル別</ToggleButton>
              <ToggleButton value="difficulty">難易度別</ToggleButton>
              <ToggleButton value="topic">問題別Top5</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup value={period} exclusive onChange={handlePeriodChange} size="small">
              <ToggleButton value={7}>7日間</ToggleButton>
              <ToggleButton value={30}>30日間</ToggleButton>
              <ToggleButton value={90}>90日間</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
              interval={getXAxisInterval()}
            />
            <YAxis
              allowDecimals={false}
              domain={[0, (dataMax: number) => Math.max(50, Math.ceil(dataMax * 1.1))]}
            />
            <Tooltip
              labelFormatter={(value) => {
                const date = new Date(value as string);
                return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <Legend
              iconType="line"
              itemSorter={(item) => {
                const value = item.value || "";
                // 難易度別の場合は順序を維持（ソートしない）
                if (viewType === "difficulty") {
                  const index = dataKeys.indexOf(value);
                  return index >= 0 ? index : 999;
                }
                // ジャンル別・合計の場合はアルファベット順
                return value;
              }}
            />
            {dataKeys.map((key, index) => (
              <Line
                key={`${viewType}-${key}`}
                type="monotone"
                dataKey={key}
                name={key}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default ResponseStatsChart;
